import { type ParsedLocation, notFound, redirect, rootRouteId } from '@tanstack/react-router';
import {
  ACCESS_ROUTES,
  canAccessFlatWorkspaceGrid,
  canAccessPortfolio,
  canAccessPortfolioGrid,
  canAccessWorkspace,
  canOpenUserManagement,
  canViewOrganizationReports,
  findWorkspaceBySlug,
  resolveFallbackRoute,
} from '@vizit/access';
import { ensureMeContext } from '@vizit/api';
import type { RouteContext } from '../../router-provider/types';
import { rememberLastWorkspaceSlug } from '../legacy-redirect/last-workspace';

async function getMeFromContext(context: RouteContext) {
  return ensureMeContext(context.queryClient, context.auth?.userId);
}

export async function ensureAuthenticatedAccessContext(context: RouteContext, location?: ParsedLocation) {
  const { beforeLoadAuthenticatedValidation } = await import('../before-load/before-load');
  await beforeLoadAuthenticatedValidation(context, location);
  return getMeFromContext(context);
}

export async function redirectFromLanding(context: RouteContext) {
  const me = await ensureAuthenticatedAccessContext(context);
  const route = resolveFallbackRoute(me);

  throw redirect({ to: route ?? ACCESS_ROUTES.noAccess, replace: true });
}

export async function guardPortfolioGrid(context: RouteContext) {
  const me = await ensureAuthenticatedAccessContext(context);

  if (!canAccessPortfolioGrid(me)) {
    throw redirect({ to: ACCESS_ROUTES.noAccess });
  }
}

export async function guardFlatWorkspaceGrid(context: RouteContext) {
  const me = await ensureAuthenticatedAccessContext(context);

  if (!canAccessFlatWorkspaceGrid(me)) {
    throw redirect({ to: ACCESS_ROUTES.noAccess });
  }
}

export async function guardPortfolioWorkspaces(context: RouteContext, portfolioId: string) {
  const me = await ensureAuthenticatedAccessContext(context);

  if (!canAccessPortfolio(me, portfolioId)) {
    throw redirect({ to: ACCESS_ROUTES.noAccess });
  }
}

export async function guardUserManagement(context: RouteContext) {
  const me = await ensureAuthenticatedAccessContext(context);

  if (!canOpenUserManagement(me)) {
    throw redirect({ to: ACCESS_ROUTES.noAccess });
  }
}

export async function guardOrganizationReports(context: RouteContext) {
  const me = await ensureAuthenticatedAccessContext(context);

  if (!canViewOrganizationReports(me)) {
    throw redirect({ to: ACCESS_ROUTES.noAccess });
  }
}

export async function guardWorkspaceShell(context: RouteContext, workspaceSlug: string, location?: ParsedLocation) {
  const me = await ensureAuthenticatedAccessContext(context, location);
  const workspace = findWorkspaceBySlug(me, workspaceSlug);

  if (!workspace || !canAccessWorkspace(me, workspace.id)) {
    // A notFound() from beforeLoad is handed to the route named by routeId and then bubbled parent by parent to the
    // root, which needs a notFoundComponent on each of them. Aiming at the root renders the 404 page directly.
    throw notFound({ routeId: rootRouteId });
  }

  rememberLastWorkspaceSlug(workspace.slug);

  return workspace;
}
